/**
 * PWA install + display-mode helpers.
 *
 * The `beforeinstallprompt` event fires once, early, on page load — long before
 * any settings component mounts. So we capture it at module scope (this file is
 * imported from `index.js`) and let React subscribe to it via `usePwaInstall`.
 */

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

type Listener = () => void;

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let didInstall = false;
const listeners = new Set<Listener>();

function emit(): void {
  listeners.forEach((l) => l());
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e: Event) => {
    // Stop Chrome's mini-infobar so we can surface our own prompt where we want it.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    didInstall = true;
    emit();
    void reportInstallToBackend();
  });
}

// Report a PWA install to the backend so it can fire an admin tg_logger line.
// Best-effort: deduped per session, login-gated (the API client only attaches a
// token when present; logged-out installs 401 and are swallowed), errors silent.
// iOS Safari never fires `appinstalled`, so this only covers Android/desktop Chromium.
async function reportInstallToBackend(): Promise<void> {
  if (typeof sessionStorage !== "undefined" && sessionStorage.getItem("pwa.installed.reported") === "1") return;
  try {
    const { apiClient } = await import("../api/client");
    await apiClient.post("/api/user/activity/pwa-installed", {
      userAgent: navigator.userAgent,
      standalone: isStandalone(),
      installedAt: new Date().toISOString(),
    });
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem("pwa.installed.reported", "1");
    }
  } catch {
    // best-effort, silent — installation succeeded regardless
  }
}

export function getDeferredPrompt(): BeforeInstallPromptEvent | null {
  return deferredPrompt;
}

export function wasInstalledThisSession(): boolean {
  return didInstall;
}

/** Subscribe to install-availability changes. Returns an unsubscribe fn. */
export function subscribePwa(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Trigger the native install prompt (Android / desktop Chromium). */
export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const evt = deferredPrompt;
  if (!evt) return "unavailable";
  try {
    await evt.prompt();
    const choice = await evt.userChoice.catch(() => ({ outcome: "dismissed" as const }));
    return choice.outcome;
  } finally {
    // The prompt can only be used once; drop it either way.
    deferredPrompt = null;
    emit();
  }
}

/* ------------------------------------------------------------------ */
/* Detection helpers — read live each call (cheap, no caching needed). */
/* ------------------------------------------------------------------ */

/** True when running as an installed app (standalone / added to home screen). */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const mm = window.matchMedia;
  const displayModeInstalled = !!mm && (
    mm("(display-mode: standalone)").matches ||
    mm("(display-mode: minimal-ui)").matches ||
    mm("(display-mode: fullscreen)").matches
  );
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return displayModeInstalled || iosStandalone;
}

export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports as MacIntel but has touch.
    (navigator.platform === "MacIntel" &&
      (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1)
  );
}

export function isAndroid(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent);
}

export function isMobile(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || isIOS();
}

/** Safari family on this device, excluding Chrome/Edge/Firefox skins. */
export function isSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/chrome|chromium|crios|fxios|edgios|edg\//i.test(ua)) return false;
  if (/safari/i.test(ua)) return true;
  // Some iOS in-app webviews drop the "Safari" token but are still WebKit.
  return isIOS();
}
